// src/pages/DuyurularPage.js
import React, { useEffect, useMemo, useState } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  List,
  Datagrid,
  EditButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useRefresh,
  useNotify,
  useRedirect,
  EditBase,
  useEditContext,
  useUpdate,
  FunctionField,
  useListContext,
} from "react-admin";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HistoryIcon from "@mui/icons-material/History";
import ImageIcon from "@mui/icons-material/Image";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import SaveIcon from "@mui/icons-material/Save";

import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

/* ==========================================================================
   TASARIM SABİTLERİ
   ========================================================================== */

const COLORS = {
  navy: "#0b1f3a",
  navySoft: "#15345d",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  green: "#10b981",
  red: "#ef4444",
  purple: "#7c3aed",
  purpleSoft: "#f5f3ff",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f3f6fb",
  border: "#e2e8f0",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    transition: "box-shadow .2s ease",

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },

  "& .MuiInputLabel-root": {
    fontWeight: 650,
  },
};

const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";
const ALLOWED = ["tr", "en", "de"];

/* ==========================================================================
   DİL METİNLERİ
   ========================================================================== */

const T = {
  tr: {
    listTitle: "Etkinlikler",
    listSubtitle:
      "Etkinliklerinizi, duyurularınızı ve slayt içeriklerinizi yönetin",
    totalLabel: "Toplam etkinlik",

    newBtn: "Yeni Etkinlik",
    exportBtn: "Dışa Aktar",

    save: "Kaydet",
    saving: "Kaydediliyor…",
    cancel: "Vazgeç",

    editTitle: "Etkinlik Düzenle",
    editSubtitle: "Etkinlik içeriğini ve yayın ayarlarını güncelleyin",
    updatedOk: "✅ Güncelleme başarılı",

    uploadImage: "Görsel Yükle",
    image: "Resim",
    title: "Başlık",
    description: "Açıklama",

    previousCol: "Önceki",
    previousTitle: "Daha önceki etkinlik başlığı",

    link: "Link",
    status: "Durum",
    contentLang: "İçerik Dili",
    contentLangShort: "İçerik Dili",

    edit: "Düzenle",
    delete: "Sil",
    operations: "İşlemler",
    confirmDelete: "Silmek istediğinize emin misiniz?",

    createTitle: "Yeni Etkinlik Ekle",
    createSubtitle:
      "Ana sayfa ve mobil uygulama için yeni etkinlik oluşturun",

    createdOk: "✅ Etkinlik başarıyla oluşturuldu",
    createError: "❌ Etkinlik eklenirken hata oluştu",
    updateError: "❌ Güncelleme hatası",

    date: "Tarih",
    active: "Aktif",
    passive: "Pasif",

    formInfo: "Etkinlik Bilgileri",
    publishInfo: "Yayın ve Görsel Ayarları",

    preview: "Canlı Önizleme",
    previewInfo:
      "Etkinlik kartınızın uygulamadaki görünümünü kontrol edin.",

    loading: "Yükleniyor…",

    emptyTitle: "Slaytlar için etkinlik eklemeye hoş geldiniz",
    emptyDesc:
      "Buraya ekleyeceğiniz görseller ve metinler ana sayfanızdaki slayt bölümünde yayınlanacaktır.",
    emptyBtn: "Hemen Etkinlik Ekle",

    langNames: {
      tr: "Türkçe",
      en: "İngilizce",
      de: "Almanca",
    },

    placeholders: {
      title: {
        tr: "Türkçe başlık girin",
        en: "İngilizce başlık girin",
        de: "Almanca başlık girin",
      },

      desc: {
        tr: "Türkçe açıklama yazın",
        en: "İngilizce açıklama yazın",
        de: "Almanca açıklama yazın",
      },
    },
  },

  en: {
    listTitle: "Events",
    listSubtitle:
      "Manage your events, announcements and slider content",
    totalLabel: "Total events",

    newBtn: "New Event",
    exportBtn: "Export",

    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",

    editTitle: "Edit Event",
    editSubtitle: "Update event content and publishing settings",
    updatedOk: "✅ Update successful",

    uploadImage: "Upload Image",
    image: "Image",
    title: "Title",
    description: "Description",

    previousCol: "Previous",
    previousTitle: "Previous event title",

    link: "Link",
    status: "Status",
    contentLang: "Content Language",
    contentLangShort: "Content Language",

    edit: "Edit",
    delete: "Delete",
    operations: "Actions",
    confirmDelete: "Are you sure you want to delete?",

    createTitle: "Create New Event",
    createSubtitle:
      "Create a new event for the homepage and mobile application",

    createdOk: "✅ Event created successfully",
    createError: "❌ Failed to add event",
    updateError: "❌ Update error",

    date: "Date",
    active: "Active",
    passive: "Inactive",

    formInfo: "Event Information",
    publishInfo: "Publishing and Image Settings",

    preview: "Live Preview",
    previewInfo:
      "Check how your event card appears in the application.",

    loading: "Loading…",

    emptyTitle: "Welcome to adding events for your slider",
    emptyDesc:
      "Items you add here will appear in the slider section of your homepage.",
    emptyBtn: "Add Event",

    langNames: {
      tr: "Turkish",
      en: "English",
      de: "German",
    },

    placeholders: {
      title: {
        tr: "Enter Turkish title",
        en: "Enter English title",
        de: "Enter German title",
      },

      desc: {
        tr: "Write description in Turkish",
        en: "Write description in English",
        de: "Write description in German",
      },
    },
  },

  de: {
    listTitle: "Veranstaltungen",
    listSubtitle:
      "Verwalten Sie Veranstaltungen, Mitteilungen und Slider-Inhalte",
    totalLabel: "Veranstaltungen insgesamt",

    newBtn: "Neue Veranstaltung",
    exportBtn: "Exportieren",

    save: "Speichern",
    saving: "Wird gespeichert…",
    cancel: "Abbrechen",

    editTitle: "Veranstaltung bearbeiten",
    editSubtitle:
      "Inhalt und Veröffentlichungseinstellungen aktualisieren",
    updatedOk: "✅ Erfolgreich aktualisiert",

    uploadImage: "Bild hochladen",
    image: "Bild",
    title: "Titel",
    description: "Beschreibung",

    previousCol: "Frühere",
    previousTitle: "Titel der früheren Veranstaltung",

    link: "Link",
    status: "Status",
    contentLang: "Inhaltssprache",
    contentLangShort: "Inhaltssprache",

    edit: "Bearbeiten",
    delete: "Löschen",
    operations: "Aktionen",
    confirmDelete: "Möchten Sie wirklich löschen?",

    createTitle: "Neue Veranstaltung erstellen",
    createSubtitle:
      "Erstellen Sie eine neue Veranstaltung für Startseite und App",

    createdOk: "✅ Veranstaltung erfolgreich erstellt",
    createError: "❌ Veranstaltung konnte nicht hinzugefügt werden",
    updateError: "❌ Aktualisierungsfehler",

    date: "Datum",
    active: "Aktiv",
    passive: "Inaktiv",

    formInfo: "Veranstaltungsinformationen",
    publishInfo: "Veröffentlichungs- und Bildeinstellungen",

    preview: "Live-Vorschau",
    previewInfo:
      "Prüfen Sie die Darstellung Ihrer Veranstaltung in der App.",

    loading: "Wird geladen…",

    emptyTitle: "Willkommen zum Hinzufügen von Veranstaltungen",
    emptyDesc:
      "Hier hinzugefügte Elemente erscheinen im Slider-Bereich Ihrer Startseite.",
    emptyBtn: "Veranstaltung hinzufügen",

    langNames: {
      tr: "Türkisch",
      en: "Englisch",
      de: "Deutsch",
    },

    placeholders: {
      title: {
        tr: "Türkischen Titel eingeben",
        en: "Englischen Titel eingeben",
        de: "Deutschen Titel eingeben",
      },

      desc: {
        tr: "Beschreibung auf Türkisch schreiben",
        en: "Beschreibung auf Englisch schreiben",
        de: "Beschreibung auf Deutsch schreiben",
      },
    },
  },
};

/* ==========================================================================
   DİL KONTROLÜ
   ========================================================================== */

function useUiTexts() {
  const [uiLang, setUiLang] = useState(() =>
    (localStorage.getItem("app_lang") || "tr")
      .slice(0, 2)
      .toLowerCase(),
  );

  useEffect(() => {
    const sync = () => {
      const nextLang = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();

      setUiLang(ALLOWED.includes(nextLang) ? nextLang : "tr");
    };

    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return useMemo(
    () => T[ALLOWED.includes(uiLang) ? uiLang : "tr"],
    [uiLang],
  );
}

/* ==========================================================================
   KAYIT YARDIMCILARI
   ========================================================================== */

function pickLocalized(record, prefix) {
  if (!record) return "";

  const pick = (code) => record[`${prefix}_${code}`];

  const order = [record.dil, "tr", "en", "de"].filter(Boolean);

  for (const code of order) {
    const value = pick(code);

    if (
      value &&
      value.trim() &&
      value.trim() !== PLACEHOLDER
    ) {
      return value;
    }
  }

  return "";
}

function pickRecordTitle(record) {
  return pickLocalized(record, "baslik") || "—";
}

function pickRecordDescription(record) {
  return pickLocalized(record, "aciklama");
}

function pickPreviousTitle(record) {
  return pickLocalized(record, "onceki") || "—";
}

function detectPreferredLang(record = {}) {
  const candidate = String(record.dil || "").toLowerCase();

  if (ALLOWED.includes(candidate)) {
    return candidate;
  }

  for (const code of ALLOWED) {
    const value = record[`baslik_${code}`];

    if (
      value &&
      value !== PLACEHOLDER
    ) {
      return code;
    }
  }

  return "tr";
}

function formatDate(value) {
  return value
    ? String(value).slice(0, 10)
    : "—";
}

/* ==========================================================================
   ÜST İŞLEM BUTONLARI
   ========================================================================== */

const ListActions = () => {
  const TT = useUiTexts();

  return (
    <TopToolbar
      sx={{
        width: "100%",

        px: {
          xs: 1.5,
          sm: 0,
        },

        py: 1.25,
        gap: 1,
        justifyContent: "flex-end",
        flexWrap: "wrap",
      }}
    >
      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.newBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.5,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 850,
          bgcolor: `${COLORS.orange} !important`,
          color: `${COLORS.navy} !important`,
          boxShadow: "0 8px 20px rgba(245,158,11,.22)",

          "&:hover": {
            bgcolor: "#fbbf24 !important",
          },
        }}
      />

      <ExportButton
        icon={<FileDownloadIcon />}
        label={TT.exportBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.25,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.blue} !important`,
          color: "#fff !important",
          boxShadow: "0 8px 20px rgba(37,99,235,.18)",

          "&:hover": {
            bgcolor: "#1d4ed8 !important",
          },
        }}
      />
    </TopToolbar>
  );
};

/* ==========================================================================
   SİLME BUTONU
   DELETE /etkinlikler/:id AYNEN KORUNDU
   ========================================================================== */

const CustomDeleteButton = ({
  record,
  compact = false,
}) => {
  const TT = useUiTexts();
  const refresh = useRefresh();

  const handleDelete = async (event) => {
    event?.stopPropagation?.();

    const confirmed = window.confirm(TT.confirmDelete);

    if (!confirmed) return;

    const token = localStorage.getItem("token");

    await fetch(
      `${API_BASE}/etkinlikler/${record.id}`,

      {
        method: "DELETE",

        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    refresh();
  };

  if (compact) {
    return (
      <Tooltip title={TT.delete}>
        <IconButton
          color="error"
          onClick={handleDelete}
          sx={{
            bgcolor: "#fff1f2",

            "&:hover": {
              bgcolor: "#ffe4e6",
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={handleDelete}
      startIcon={<DeleteIcon />}
      color="error"
      variant="contained"
      size="small"
      sx={{
        minHeight: 38,
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 800,
        boxShadow: "none",
      }}
    >
      {TT.delete}
    </Button>
  );
};

/* ==========================================================================
   DÜZENLE / SİL BUTONLARI
   ========================================================================== */

const ActionButtons = ({
  compact = false,
  record: recordProp,
}) => {
  const TT = useUiTexts();
  const contextRecord = useRecordContext();
  const record = recordProp || contextRecord;

  if (!record) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
    >
      <EditButton
        record={record}
        icon={<EditIcon />}
        label={compact ? "" : TT.edit}
        onClick={(event) => event.stopPropagation()}
        sx={{
          minWidth: compact ? 42 : "auto",
          minHeight: 38,
          px: compact ? 0.75 : 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.orangeSoft} !important`,
          color: "#b45309 !important",

          "& .MuiButton-startIcon": {
            mr: compact ? 0 : 1,
          },

          "&:hover": {
            bgcolor: "#ffedd5 !important",
          },
        }}
      />

      <CustomDeleteButton
        record={record}
        compact={compact}
      />
    </Stack>
  );
};

/* ==========================================================================
   TABLO ALANLARI
   ========================================================================== */

const BaslikField = ({ label }) => {
  const record = useRecordContext();

  if (!record) return null;

  const title = pickRecordTitle(record);
  const description = pickRecordDescription(record);

  return (
    <Box
      aria-label={label}
      title={title}
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: COLORS.text,
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.4,
            color: COLORS.textSoft,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 360,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const OncekiField = ({ label }) => {
  const record = useRecordContext();

  if (!record) return null;

  const value = pickPreviousTitle(record);

  return (
    <Typography
      aria-label={label}
      title={value}
      variant="body2"
      sx={{
        fontWeight: 750,
        color: COLORS.textSoft,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  );
};

const DilField = ({
  label,
  names,
}) => {
  const record = useRecordContext();

  if (!record) return null;

  const code = detectPreferredLang(record);

  return (
    <Chip
      icon={<LanguageIcon />}
      label={names[code] || "—"}
      aria-label={label}
      size="small"
      sx={{
        fontWeight: 800,
        color: COLORS.navy,
        bgcolor: COLORS.blueSoft,

        "& .MuiChip-icon": {
          color: COLORS.blue,
        },
      }}
    />
  );
};

const StatusField = () => {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) return null;

  return (
    <Chip
      icon={
        record.aktif
          ? <CheckCircleIcon />
          : undefined
      }
      label={
        record.aktif
          ? TT.active
          : TT.passive
      }
      size="small"
      sx={{
        fontWeight: 850,

        color: record.aktif
          ? "#047857"
          : COLORS.textSoft,

        bgcolor: record.aktif
          ? "#ecfdf5"
          : "#f1f5f9",

        "& .MuiChip-icon": {
          color: COLORS.green,
        },
      }}
    />
  );
};

const EventImageField = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box
      sx={{
        width: 68,
        height: 68,
        borderRadius: 2.5,
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        bgcolor: "#f1f5f9",
        display: "grid",
        placeItems: "center",
      }}
    >
      {record.resim ? (
        <Box
          component="img"
          src={record.resim}
          alt={pickRecordTitle(record)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <ImageIcon
          sx={{
            color: "#94a3b8",
          }}
        />
      )}
    </Box>
  );
};

/* ==========================================================================
   BOŞ LİSTE
   ========================================================================== */

const EmptyState = () => {
  const TT = useUiTexts();

  return (
    <Box
      sx={{
        minHeight: "55vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: COLORS.page,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 680,

          p: {
            xs: 3,
            sm: 5,
          },

          borderRadius: 4,
          borderStyle: "dashed",
          borderColor: "#cbd5e1",
          textAlign: "center",
          boxShadow: "0 16px 40px rgba(15,23,42,.05)",
        }}
      >
        <Box
          sx={{
            width: 82,
            height: 82,
            borderRadius: 3,
            bgcolor: COLORS.purpleSoft,
            color: COLORS.purple,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <EventIcon
            sx={{
              fontSize: 42,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {TT.emptyTitle}
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            color: COLORS.textSoft,
            lineHeight: 1.7,
          }}
        >
          {TT.emptyDesc}
        </Typography>

        <CreateButton
          label={TT.emptyBtn}
          icon={<AddCircleIcon />}
          sx={{
            mt: 2.5,
            minHeight: 46,
            px: 3,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 850,
            bgcolor: `${COLORS.orange} !important`,
            color: `${COLORS.navy} !important`,
          }}
        />
      </Paper>
    </Box>
  );
};

/* ==========================================================================
   LİSTE BAŞLIĞI
   ========================================================================== */

function ListHeader() {
  const TT = useUiTexts();
  const { total, data } = useListContext();

  const count = total ?? data?.length ?? 0;

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 3,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #5b21b6 120%)",

        borderBottom: `4px solid ${COLORS.orange}`,
        position: "relative",
        overflow: "hidden",

        "&::after": {
          content: '""',
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          right: -100,
          top: -150,
          bgcolor: "rgba(255,255,255,.1)",
        },
      }}
    >
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
        >
          <Box
            sx={{
              width: {
                xs: 48,
                sm: 56,
              },

              height: {
                xs: 48,
                sm: 56,
              },

              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: "#fbbf24",
              bgcolor: "rgba(255,255,255,.11)",
              border: "1px solid rgba(255,255,255,.18)",
              flexShrink: 0,
            }}
          >
            <EventIcon fontSize="large" />
          </Box>

          <Box>
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.55rem",
                  sm: "2rem",
                },

                fontWeight: 950,
              }}
            >
              {TT.listTitle}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.4,
                color: "rgba(255,255,255,.76)",
              }}
            >
              {TT.listSubtitle}
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={`${TT.totalLabel}: ${count}`}
          sx={{
            color: "#fff",
            fontWeight: 850,
            bgcolor: "rgba(255,255,255,.12)",
            border: "1px solid rgba(255,255,255,.2)",
          }}
        />
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   MOBİL ETKİNLİK KARTI
   ========================================================================== */

function MobileEventCard({ record }) {
  const TT = useUiTexts();

  const title = pickRecordTitle(record);
  const description = pickRecordDescription(record);
  const previous = pickPreviousTitle(record);
  const language = detectPreferredLang(record);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3.5,
        borderColor: COLORS.border,
        boxShadow: "0 10px 28px rgba(15,23,42,.055)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: 5,

          background: record.aktif
            ? "linear-gradient(90deg, #7c3aed, #2563eb)"
            : "linear-gradient(90deg, #94a3b8, #cbd5e1)",
        }}
      />

      {record.resim ? (
        <Box
          component="img"
          src={record.resim}
          alt={title}
          sx={{
            width: "100%",
            height: 190,
            objectFit: "cover",
          }}
        />
      ) : (
        <Box
          sx={{
            height: 150,
            display: "grid",
            placeItems: "center",
            bgcolor: "#e2e8f0",
            color: "#94a3b8",
          }}
        >
          <ImageIcon
            sx={{
              fontSize: 48,
            }}
          />
        </Box>
      )}

      <CardContent
        sx={{
          p: 2,

          "&:last-child": {
            pb: 2,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 950,
                color: COLORS.text,
                fontSize: "1.08rem",
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>

            {description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.55,
                  color: COLORS.textSoft,
                  lineHeight: 1.6,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {description}
              </Typography>
            )}
          </Box>

          <ActionButtons
            compact
            record={record}
          />
        </Stack>

        {previous !== "—" && (
          <Paper
            variant="outlined"
            sx={{
              mt: 1.5,
              p: 1.15,
              borderRadius: 2.5,
              borderColor: COLORS.border,
              bgcolor: "#f8fafc",
            }}
          >
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
            >
              <HistoryIcon
                fontSize="small"
                sx={{
                  color: COLORS.textSoft,
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  color: COLORS.textSoft,
                }}
              >
                {previous}
              </Typography>
            </Stack>
          </Paper>
        )}

        <Divider
          sx={{
            my: 1.5,
            borderColor: COLORS.border,
          }}
        />

        <Stack
          direction="row"
          spacing={0.7}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            icon={<CalendarMonthIcon />}
            label={formatDate(record.tarih)}
            sx={{
              fontWeight: 800,
              bgcolor: COLORS.purpleSoft,
            }}
          />

          <Chip
            size="small"
            icon={<LanguageIcon />}
            label={TT.langNames[language]}
            sx={{
              fontWeight: 800,
              bgcolor: COLORS.blueSoft,

              "& .MuiChip-icon": {
                color: COLORS.blue,
              },
            }}
          />

          <Chip
            size="small"
            label={
              record.aktif
                ? TT.active
                : TT.passive
            }
            sx={{
              fontWeight: 850,

              color: record.aktif
                ? "#047857"
                : COLORS.textSoft,

              bgcolor: record.aktif
                ? "#ecfdf5"
                : "#f1f5f9",
            }}
          />
        </Stack>

        {record.link && (
          <Stack
            direction="row"
            spacing={0.7}
            alignItems="center"
            sx={{
              mt: 1.25,
            }}
          >
            <LinkIcon
              fontSize="small"
              sx={{
                color: COLORS.blue,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: COLORS.blue,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {record.link}
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/* ==========================================================================
   MOBİL VE MASAÜSTÜ LİSTE
   ========================================================================== */

function ResponsiveEventList() {
  const TT = useUiTexts();
  const { data = [] } = useListContext();

  return (
    <>
      {/* MOBİL VE TABLET */}

      <Box
        sx={{
          display: {
            xs: "block",
            lg: "none",
          },

          p: 1.5,
        }}
      >
        <Grid
          container
          spacing={1.5}
        >
          {data.map((record) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={record.id}
            >
              <MobileEventCard record={record} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* MASAÜSTÜ TABLO */}

      <Box
        sx={{
          display: {
            xs: "none",
            lg: "block",
          },

          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",

          "&::-webkit-scrollbar": {
            height: 8,
          },

          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            bgcolor: "#cbd5e1",
          },
        }}
      >
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          isRowSelectable={() => false}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: "100%",
            tableLayout: "fixed",

            "& .MuiTable-root": {
              width: "100%",
              maxWidth: "100%",
              tableLayout: "fixed",
            },

            "& .RaDatagrid-headerCell": {
              py: 1.6,
              bgcolor: "#f8fafc",
              color: COLORS.textSoft,
              fontWeight: 900,
              fontSize: ".76rem",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .RaDatagrid-row": {
              transition: "background-color .2s ease",

              "&:hover": {
                bgcolor: "#f8fafc",
              },
            },

            "& .MuiTableCell-root": {
              py: 1.2,
              px: 1.35,
              verticalAlign: "middle",
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .column-resim": {
              width: 68,
              minWidth: 68,
              maxWidth: 68,
            },

            "& .column-baslik": {
              width: "auto",
            },

            "& .column-onceki": {
              width: "25%",
            },

            "& .column-link": {
              width: "18%",
            },

            "& .column-tarih": {
              width: 96,
              minWidth: 96,
              maxWidth: 96,
            },

            "& .column-aktif": {
              width: 78,
              minWidth: 78,
              maxWidth: 78,
            },

            "& .column-lang": {
              width: 92,
              minWidth: 92,
              maxWidth: 92,
            },

            "& .column-actions, & th:last-of-type, & td:last-of-type": {
              position: "sticky",
              right: 0,
              zIndex: 3,
              width: 156,
              minWidth: 156,
              maxWidth: 156,
              bgcolor: "#fff",
              boxShadow: "-8px 0 14px rgba(15,23,42,.06)",
            },

            "& .RaDatagrid-headerCell.column-actions, & thead th:last-of-type": {
              zIndex: 6,
              bgcolor: "#f8fafc",
            },

            "& .column-actions .MuiStack-root": {
              width: "100%",
              justifyContent: "flex-end",
              gap: 0.5,
            },

            "& .column-actions .MuiButton-root": {
              minWidth: 0,
              px: 1.15,
            },

            /* Standart PC ekranlarında uzun açıklama gizlenir. */
            "@media (max-width: 1799px)": {
              minWidth: "100%",

              "& .column-onceki": {
                display: "none",
              },

              "& .column-baslik": {
                width: "auto",
              },

              "& .column-link": {
                width: "22%",
              },
            },

            /* Laptoplarda link de gizlenir; Düzenle/Sil daima görünür kalır. */
            "@media (max-width: 1599px)": {
              minWidth: "100%",

              "& .column-link": {
                display: "none",
              },

              "& .column-baslik": {
                width: "auto",
              },
            },

            /* Küçük laptopta yalnızca temel yönetim kolonları gösterilir. */
            "@media (max-width: 1299px)": {
              "& .column-lang": {
                display: "none",
              },
            },
          }}
        >
          <FunctionField
            label={TT.image}
            className="column-resim"
            render={() => <EventImageField />}
          />

          <FunctionField
            label={TT.title}
            className="column-baslik"
            render={() => (
              <BaslikField label={TT.title} />
            )}
          />

          <FunctionField
            label={TT.previousCol}
            className="column-onceki"
            render={() => (
              <OncekiField label={TT.previousCol} />
            )}
          />

          <FunctionField
            label={TT.link}
            className="column-link"
            render={(record) => (
              <Typography
                variant="body2"
                title={record?.link || ""}
                sx={{
                  color: record?.link
                    ? COLORS.blue
                    : COLORS.textSoft,

                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record?.link || "—"}
              </Typography>
            )}
          />

          <FunctionField
            label={TT.date}
            className="column-tarih"
            render={(record) => (
              <Chip
                size="small"
                icon={<CalendarMonthIcon />}
                label={formatDate(record?.tarih)}
                sx={{
                  fontWeight: 800,
                  bgcolor: COLORS.purpleSoft,
                }}
              />
            )}
          />

          <FunctionField
            label={TT.status}
            className="column-aktif"
            render={() => <StatusField />}
          />

          <FunctionField
            label={TT.contentLang}
            className="column-lang"
            render={() => (
              <DilField
                label={TT.contentLang}
                names={TT.langNames}
              />
            )}
          />

          <FunctionField
            label={TT.operations}
            className="column-actions"
            render={() => <ActionButtons />}
          />
        </Datagrid>
      </Box>
    </>
  );
}

/* ==========================================================================
   LİSTE SAYFASI
   ========================================================================== */

export const EtkinliklerPage = (props) => {
  const TT = useUiTexts();

  return (
    <>
      <PageGlobalStyles />

      <List
        {...props}
        resource="etkinlikler"
        title={TT.listTitle}
        actions={<ListActions />}
        sort={{
          field: "tarih",
          order: "DESC",
        }}
        empty={<EmptyState />}
        sx={{
          m: 0,
          p: 0,
          width: "100%",
          maxWidth: "100%",
          bgcolor: COLORS.page,

          "& .RaList-main": {
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          },

          "& .RaList-content": {
            m: 0,
            p: 0,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",

            borderRadius: {
              xs: 0,
              md: 3.5,
            },

            border: {
              md: `1px solid ${COLORS.border}`,
            },

            boxShadow: {
              md: "0 18px 48px rgba(15,23,42,.07)",
            },
          },

          "& .MuiCard-root": {
            boxShadow: "none",
          },

          "& .MuiContainer-root": {
            maxWidth: "100% !important",
            px: "0 !important",
          },
        }}
      >
        <Box>
          <ListHeader />
          <ResponsiveEventList />
        </Box>
      </List>
    </>
  );
};

/* ==========================================================================
   GLOBAL TASARIM
   ========================================================================== */

function PageGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        html: {
          overflowX: "hidden",
        },

        body: {
          overflowX: "hidden",
          background: COLORS.page,
        },

        "*": {
          boxSizing: "border-box",
        },

        ".MuiSnackbarContent-message": {
          fontSize: "1rem",
          fontWeight: 800,
        },

        ".RaNotification-content": {
          fontSize: "1rem",
          fontWeight: 800,
        },
      }}
    />
  );
}

/* ==========================================================================
   FORM SAYFASI BAŞLIĞI
   ========================================================================== */

function FormPageHeader({
  title,
  subtitle,
  TT,
  onBack,
}) {
  return (
    <Box
      sx={{
        p: {
          xs: 2.25,
          sm: 3.5,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #5b21b6 120%)",

        borderBottom: `4px solid ${COLORS.orange}`,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Tooltip title={TT.cancel}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,.12)",
              border: "1px solid rgba(255,255,255,.18)",

              "&:hover": {
                bgcolor: "rgba(255,255,255,.2)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "1.45rem",
                sm: "2rem",
              },

              fontWeight: 950,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: "rgba(255,255,255,.76)",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   FORM BÖLÜM BAŞLIĞI
   ========================================================================== */

function FormSectionTitle({
  icon,
  title,
  subtitle,
}) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        mb: 2.25,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.purpleSoft,
          color: COLORS.purple,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: COLORS.textSoft,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/* ==========================================================================
   ORTAK ETKİNLİK FORMU
   ========================================================================== */

function EventForm({
  form,
  setForm,
  activeLanguages,
  onSubmit,
  saving,
  active,
  onActiveChange,
  TT,
  onCancel,
}) {
  const titleKey = `baslik_${form.dil}`;
  const descriptionKey = `aciklama_${form.dil}`;
  const previousKey = `onceki_${form.dil}`;

  const availableLanguages =
    activeLanguages?.filter((code) =>
      ALLOWED.includes(code),
    ) || ALLOWED;

  const setValue = (name, value) => {
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 7fr) minmax(0, 5fr)",
          },
          gap: 2.5,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {/* ETKİNLİK BİLGİLERİ */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<CampaignIcon />}
              title={TT.formInfo}
              subtitle={TT.contentLang}
            />

            <Stack spacing={2.2}>
              <MuiTextField
                select
                fullWidth
                label={TT.contentLang}
                name="dil"
                value={form.dil}
                onChange={(event) =>
                  setValue(
                    "dil",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              >
                {availableLanguages.map((code) => (
                  <MenuItem
                    key={code}
                    value={code}
                  >
                    {TT.langNames[code]}
                  </MenuItem>
                ))}
              </MuiTextField>

              <MuiTextField
                fullWidth
                required
                label={TT.title}
                name={titleKey}
                value={form[titleKey] || ""}
                placeholder={
                  TT.placeholders.title[form.dil]
                }
                onChange={(event) =>
                  setValue(
                    titleKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                label={TT.previousTitle}
                name={previousKey}
                value={form[previousKey] || ""}
                onChange={(event) =>
                  setValue(
                    previousKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                required
                multiline
                minRows={7}
                maxRows={14}
                label={TT.description}
                name={descriptionKey}
                value={form[descriptionKey] || ""}
                placeholder={
                  TT.placeholders.desc[form.dil]
                }
                onChange={(event) =>
                  setValue(
                    descriptionKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                label={TT.link}
                name="link"
                value={form.link || ""}
                placeholder="https://..."
                onChange={(event) =>
                  setValue(
                    "link",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />
            </Stack>
          </Paper>
        </Box>

        {/* YAYIN VE GÖRSEL AYARLARI */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<ImageIcon />}
              title={TT.publishInfo}
              subtitle={
                active
                  ? TT.active
                  : TT.passive
              }
            />

            <Stack spacing={2.2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,
                  bgcolor: "#f8fafc",
                  overflow: "hidden",

                  "& button": {
                    maxWidth: "100%",
                  },
                }}
              >
                <ResimUpload
                  label={TT.uploadImage}
                  defaultPreview={form.resim}
                  onUpload={(url) =>
                    setValue(
                      "resim",
                      url,
                    )
                  }
                />
              </Paper>

              <MuiTextField
                fullWidth
                type="date"
                label={TT.date}
                name="tarih"
                value={form.tarih || ""}
                onChange={(event) =>
                  setValue(
                    "tarih",
                    event.target.value,
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={fieldSx}
              />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,

                  bgcolor: active
                    ? "#ecfdf5"
                    : "#f8fafc",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(event) =>
                        onActiveChange(
                          event.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    active
                      ? TT.active
                      : TT.passive
                  }
                  sx={{
                    m: 0,
                    width: "100%",

                    "& .MuiFormControlLabel-label": {
                      fontWeight: 850,

                      color: active
                        ? "#047857"
                        : COLORS.textSoft,
                    },
                  }}
                />
              </Paper>

              <Divider
                sx={{
                  borderColor: COLORS.border,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: COLORS.text,
                  }}
                >
                  {TT.preview}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.textSoft,
                  }}
                >
                  {TT.previewInfo}
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3.5,
                  borderColor: COLORS.border,
                  overflow: "hidden",
                  boxShadow: "0 10px 28px rgba(15,23,42,.06)",
                }}
              >
                {form.resim ? (
                  <Box
                    component="img"
                    src={form.resim}
                    alt={
                      form[titleKey] ||
                      TT.title
                    }
                    sx={{
                      width: "100%",
                      height: 210,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 190,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#e2e8f0",
                      color: "#94a3b8",
                    }}
                  >
                    <ImageIcon
                      sx={{
                        fontSize: 54,
                      }}
                    />
                  </Box>
                )}

                <CardContent>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      color: COLORS.text,
                    }}
                  >
                    {form[titleKey] || TT.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.6,
                      color: COLORS.textSoft,
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {form[descriptionKey] || TT.description}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.7}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      mt: 1.5,
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<CalendarMonthIcon />}
                      label={form.tarih || "—"}
                      sx={{
                        fontWeight: 800,
                        bgcolor: COLORS.purpleSoft,
                      }}
                    />

                    <Chip
                      size="small"
                      label={TT.langNames[form.dil]}
                      sx={{
                        fontWeight: 800,
                        bgcolor: COLORS.blueSoft,
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Paper>
        </Box>
      </Box>

      {/* FORM BUTONLARI */}

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction={{
            xs: "column-reverse",
            sm: "row",
          }}
          justifyContent="flex-end"
          spacing={1.25}
        >
          <Button
            type="button"
            onClick={onCancel}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              minHeight: 48,
              px: 3,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 850,
              borderColor: COLORS.border,
              color: COLORS.text,
            }}
          >
            {TT.cancel}
          </Button>

          <Button
            type="submit"
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            sx={{
              minHeight: 48,
              px: 3.5,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: COLORS.navy,
              boxShadow: "0 10px 25px rgba(11,31,58,.2)",

              "&:hover": {
                bgcolor: COLORS.navySoft,
              },
            }}
          >
            {saving
              ? TT.saving
              : TT.save}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ==========================================================================
   FORM SAYFASI DIŞ YAPISI
   ========================================================================== */

function FormPageShell({
  title,
  subtitle,
  TT,
  onBack,
  children,
}) {
  return (
    <>
      <PageGlobalStyles />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: COLORS.page,
          pb: 5,
        }}
      >
        <FormPageHeader
          title={title}
          subtitle={subtitle}
          TT={TT}
          onBack={onBack}
        />

        <Container
          maxWidth="xl"
          sx={{
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",

            mt: {
              xs: 2,
              md: 3,
            },

            px: {
              xs: 1.5,
              sm: 3,
            },
          }}
        >
          {children}
        </Container>
      </Box>
    </>
  );
}

/* ==========================================================================
   ETKİNLİK DÜZENLEME
   ========================================================================== */

export const EtkinlikEdit = (props) => (
  <EditBase
    resource="etkinlikler"
    {...props}
  >
    <EtkinlikEditView />
  </EditBase>
);

function EtkinlikEditView() {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const {
    record,
    isLoading,
  } = useEditContext();

  const [
    update,
    {
      isLoading: saving,
    },
  ] = useUpdate();

  const [form, setForm] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    onceki_tr: "",
    onceki_en: "",
    onceki_de: "",

    resim: "",
    link: "",
    aktif: true,
    dil: "tr",
    tarih: "",
  });

  React.useEffect(() => {
    if (!isLoading && record) {
      const preferred = detectPreferredLang(record);

      setForm({
        baslik_tr: record.baslik_tr || "",
        baslik_en: record.baslik_en || "",
        baslik_de: record.baslik_de || "",

        aciklama_tr: record.aciklama_tr || "",
        aciklama_en: record.aciklama_en || "",
        aciklama_de: record.aciklama_de || "",

        onceki_tr: record.onceki_tr || "",
        onceki_en: record.onceki_en || "",
        onceki_de: record.onceki_de || "",

        resim: record.resim || "",
        link: record.link || "",
        aktif: !!record.aktif,
        dil: preferred,

        tarih:
          (record.tarih &&
            String(record.tarih).slice(0, 10)) ||
          "",
      });
    }
  }, [isLoading, record]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!record) return;

    const merged = {
      ...record,
      ...form,

      aktif: !!form.aktif,

      dil: ALLOWED.includes(form.dil)
        ? form.dil
        : "tr",

      tarih: form.tarih
        ? new Date(form.tarih)
            .toISOString()
            .slice(0, 10)
        : record.tarih || "",
    };

    [
      "baslik_tr",
      "baslik_en",
      "baslik_de",

      "aciklama_tr",
      "aciklama_en",
      "aciklama_de",

      "onceki_tr",
      "onceki_en",
      "onceki_de",
    ].forEach((key) => {
      if (merged[key] === PLACEHOLDER) {
        merged[key] = "";
      }
    });

    update(
      "etkinlikler",

      {
        id: record.id,
        data: merged,
        previousData: record,
      },

      {
        onSuccess: () => {
          notify(
            TT.updatedOk,
            {
              type: "success",
            },
          );

          redirect("/etkinlikler");
        },

        onError: (error) =>
          notify(
            `${TT.updateError}: ${error?.message || ""}`,

            {
              type: "warning",
            },
          ),
      },
    );
  };

  if (
    isLoading ||
    !record
  ) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.page,
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
        >
          <CircularProgress />

          <Typography color="text.secondary">
            {TT.loading}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <FormPageShell
      title={TT.editTitle}
      subtitle={TT.editSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/etkinlikler")
      }
    >
      <EventForm
        form={form}
        setForm={setForm}
        activeLanguages={ALLOWED}
        onSubmit={handleSubmit}
        saving={saving}
        active={!!form.aktif}
        onActiveChange={(checked) =>
          setForm((previous) => ({
            ...previous,
            aktif: checked,
          }))
        }
        TT={TT}
        onCancel={() =>
          redirect("/etkinlikler")
        }
      />
    </FormPageShell>
  );
}

/* ==========================================================================
   YENİ ETKİNLİK
   /kurum-ayarlar VE POST /etkinlikler KORUNDU
   ========================================================================== */

const EtkinliklerCard = () => {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const [saving, setSaving] = React.useState(false);

  const [formData, setFormData] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    onceki_tr: "",
    onceki_en: "",
    onceki_de: "",

    link: "https://",
    resim: "",
    durum: "Aktif",
    dil: "tr",

    tarih: new Date()
      .toISOString()
      .slice(0, 10),
  });

  const [
    aktifDiller,
    setAktifDiller,
  ] = React.useState([
    "tr",
    "en",
    "de",
  ]);

  React.useEffect(() => {
    const applySettings = async () => {
      const appLang = (
        localStorage.getItem("app_lang") ||
        "tr"
      )
        .slice(0, 2)
        .toLowerCase();

      let nextDil = ALLOWED.includes(appLang)
        ? appLang
        : "tr";

      let nextAktif = [
        "tr",
        "en",
        "de",
      ];

      let apiDil = null;

      try {
        const token = localStorage.getItem("token");

        if (token) {
          const response = await fetch(
            `${API_BASE}/kurum-ayarlar`,

            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (response.ok) {
            const json = await response.json();
            const data = json?.data || null;

            if (data) {
              let activeLanguages =
                data.aktif_diller;

              if (
                typeof activeLanguages === "string"
              ) {
                try {
                  activeLanguages =
                    JSON.parse(activeLanguages);
                } catch {
                  activeLanguages = [
                    "tr",
                    "en",
                    "de",
                  ];
                }
              }

              if (
                Array.isArray(activeLanguages) &&
                activeLanguages.length
              ) {
                nextAktif = activeLanguages.filter(
                  (code) => ALLOWED.includes(code),
                );
              }

              const candidate = String(
                data.dil || "",
              ).toLowerCase();

              apiDil = ALLOWED.includes(candidate)
                ? candidate
                : null;
            }
          }
        }
      } catch {
        // Ayarlar alınamazsa varsayılan diller ile devam eder.
      }

      if (nextAktif.includes(appLang)) {
        nextDil = appLang;
      } else if (
        apiDil &&
        nextAktif.includes(apiDil)
      ) {
        nextDil = apiDil;
      } else {
        nextDil = nextAktif[0] || "tr";
      }

      setAktifDiller(nextAktif);

      setFormData((previous) => ({
        ...previous,
        dil: nextDil,
      }));
    };

    applySettings();

    const sync = () => applySettings();

    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("app_settings_change", sync);
    window.addEventListener("app_lang_change", sync);

    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("app_settings_change", sync);
      window.removeEventListener("app_lang_change", sync);
    };
  }, []);

  React.useEffect(() => {
    if (!aktifDiller.includes(formData.dil)) {
      setFormData((previous) => ({
        ...previous,
        dil: aktifDiller[0] || "tr",
      }));
    }
  }, [
    aktifDiller,
    formData.dil,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const aktif =
      formData.durum === "Aktif";

    const etkinlik = {
      baslik_tr:
        formData.dil === "tr"
          ? formData.baslik_tr
          : PLACEHOLDER,

      baslik_en:
        formData.dil === "en"
          ? formData.baslik_en
          : PLACEHOLDER,

      baslik_de:
        formData.dil === "de"
          ? formData.baslik_de
          : PLACEHOLDER,

      onceki_tr:
        formData.dil === "tr"
          ? formData.onceki_tr
          : PLACEHOLDER,

      onceki_en:
        formData.dil === "en"
          ? formData.onceki_en
          : PLACEHOLDER,

      onceki_de:
        formData.dil === "de"
          ? formData.onceki_de
          : PLACEHOLDER,

      aciklama_tr:
        formData.dil === "tr"
          ? formData.aciklama_tr
          : PLACEHOLDER,

      aciklama_en:
        formData.dil === "en"
          ? formData.aciklama_en
          : PLACEHOLDER,

      aciklama_de:
        formData.dil === "de"
          ? formData.aciklama_de
          : PLACEHOLDER,

      resim: formData.resim,
      link: formData.link,
      aktif,
      tarih: formData.tarih,
      dil: formData.dil,
    };

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/etkinlikler`,

        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify(etkinlik),
        },
      );

      if (response.ok) {
        notify(
          TT.createdOk,
          {
            type: "success",
          },
        );

        redirect("/etkinlikler");
      } else {
        notify(
          TT.createError,
          {
            type: "error",
          },
        );
      }
    } catch (error) {
      console.error(
        "Sunucu hatası:",
        error,
      );

      notify(
        TT.createError,
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageShell
      title={TT.createTitle}
      subtitle={TT.createSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/etkinlikler")
      }
    >
      <EventForm
        form={formData}
        setForm={setFormData}
        activeLanguages={aktifDiller}
        onSubmit={handleSubmit}
        saving={saving}
        active={formData.durum === "Aktif"}
        onActiveChange={(checked) =>
          setFormData((previous) => ({
            ...previous,

            durum: checked
              ? "Aktif"
              : "Pasif",
          }))
        }
        TT={TT}
        onCancel={() =>
          redirect("/etkinlikler")
        }
      />
    </FormPageShell>
  );
};

export const EtkinlikCreate = () => (
  <EtkinliklerCard />
);
